import React, { useState, useEffect, useRef, useMemo } from "react";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import TableStore from "@harbor/elements/utils/table/store";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import AppHOC from "../../../generics/AppHOC";
import NoData from "../../../common/table/NoData";
import { formatRequestPayload } from "../../../utils/query-utils";
import { sortdata } from "../../../utils/common";
import { getTableColumns } from "./tableConfig";
import css from "../app360MagneticStyle.less";
import { useI18n } from "../../../i18n/useI18n";
import { formatUsageCellData } from "./tableConfig";

const [ HbrTable ] = reactWrapper(["hbr-table"]);
const tableId = "topusers_table";

const TopUsersTable = ({ data, reportView, globalFilter, app360 }) => {
  const [loader, setLoader] = useState(true);
  const [, forceUpdate] = useState({}); // Used to trigger re-render from TableStore 
  const tableRef = useRef({ error: null }); // Track fetch error
  const { tViews } = useI18n('views');
  const columnsHook = useColumns({
    config: ({ store }) => getTableColumns(tableId, tViews, reportView, store),
  });
  const tableStore = useMemo(() => {
    return new TableStore({
      query: { page: 0, pageLength: 10 },
      hooks: [columnsHook],
      fetchData: async query => {
        setLoader(true);
        try {
          const rawData = reportView ?
            app360?.top_users_table
            : data?.app360?.top_users_table;
          const sortedData = (rawData || []).map(item => ({ ...item }));
          const siteData = reportView
            ? globalFilter?.siteAPIData?.sitesTableData
            : globalFilter?.siteAPIData?.sitesTableData?.data || [];
          const requestPayload = formatRequestPayload(
            {},
            data,
            tableRef.current?.table
          );
          sortedData.forEach(item => {
            const match = siteData.find(site => site.site_id === item.site_id

            );
            if (match) item.city = match.city;
            if('usage' in item){
              item.usage = formatUsageCellData(item.usage);
            }
          });
          const finalData = sortdata(requestPayload, sortedData);
          query.setSource(finalData);
          query.setCount(finalData.length || 0);
        } catch (error) {
          console.error("Error fetching top users table:", error);
          tableRef.current.error = error;
          query.setSource([]);
          query.setCount(0);
        } finally {
          setLoader(false);
        }
      }
    });
  }, [data, app360, reportView, globalFilter]);
  useEffect(() => {
    const unsubscribe = tableStore.subscribe(() => forceUpdate({}));
    return () => unsubscribe();
  }, [tableStore]);
  const { source, columns, query } = tableStore.getState();
  const { pageLength } = query;

  return (
    <div className={css["topusers-table"]} data-cy="topusersTableView">
      <HbrTable
        id={tableId}
        data-testid={tableId}
        columns={columns}
        source={source}
        style={{
            minHeight: "300px", height: reportView ? `${source.length * 37.5}px` : "100%"
          }}
        serverSide externalSorting readonly resize frameSize={pageLength}
        canFocus={false}
        useClipboard={false}
        headerSlot role="table"
        onHbr-before-sort={event => {
          const { column, order } = event.detail;
          const columnsHook = tableStore.getHook("columns");
          if (order) {
            columnsHook.sort({ order: [[column.id, order]] });
          }
        }}
      >
        <NoData
          count={source.length}
          loader={loader}
          error={tableRef.current.error}
        />
      </HbrTable>
    </div>
  );
};

TopUsersTable.propTypes = {
  data: PropTypes.object.isRequired,
  reportView: PropTypes.bool,
  globalFilter: PropTypes.object.isRequired,
  app360: PropTypes.object.isRequired
};

export default AppHOC(TopUsersTable);
